import axios from 'axios';

// console.log(process.env.VUE_APP_API_SERVER_URL);

/**
 * Axios 인스턴스 생성
 * - baseURL: 환경변수에서 API 서버 주소를 읽어옴
 * - timeout: 요청이 1000ms(1초) 이상 걸리면 자동 중단
 */
const instance = axios.create({
    baseURL: process.env.VUE_APP_API_SERVER_URL,
    timeout: 10000,  // 10초 (기존 1초는 너무 짧아 네트워크 지연 시 실패)
});


/**
 * 요청 인터셉터
 * - 모든 요청 전에 localStorage 에서 accessToken 을 꺼내 Authorization 헤더에 자동 첨부
 */
instance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');

        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);


/**
 * 응답 인터셉터
 * - 정상 응답(2xx): 그대로 반환
 * - 401 Unauthorized 응답:
 *   1) 최초 1회에 한해 refresh-token 재발급 시도 (_retry 플래그로 무한루프 방지)
 *   2) 새 accessToken 을 localStorage 에 저장 후 원래 요청 재시도
 *   3) refresh-token 재발급도 실패하면 로그인 페이지로 이동
 *
 * [BUG FIX] 기존 코드 문제:
 *   1) refresh-token 재시도 시 `axios(originalRequest)` 사용 → 커스텀 헤더/baseURL 무시됨
 *      수정: `instance(originalRequest)` 로 변경하여 인터셉터가 적용된 인스턴스로 재시도
 *   2) 새 accessToken 을 axios.defaults 에만 설정하고 localStorage 에 저장 안 함
 *      수정: localStorage.setItem 으로 저장하여 페이지 새로고침 후에도 유지되도록 수정
 *   3) refresh-token API 호출 시에도 instance 사용하여 baseURL 통일
 */
instance.interceptors.response.use(
    (response) => {
        // 정상 응답은 그대로 반환
        return response;
    },
    async (error) => {
        // console.log("응답 인터셉터 에러:", error);

        try {
            if (error.response && error.response.status === 401) {
                const originalRequest = error.config;

                // _retry 플래그: 무한 재시도 방지 (refresh-token 요청 자체가 401이면 중단)
                if (!originalRequest._retry) {
                    originalRequest._retry = true;

                    const refreshToken = localStorage.getItem('refreshToken');

                    if (!refreshToken) {
                        // refreshToken 자체가 없으면 재로그인 필요
                        console.warn("refreshToken 없음 → 로그인 페이지로 이동");
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        window.location.href = '/loginPage';
                        return Promise.reject(error);
                    }

                    // [BUG FIX] axios → instance 로 변경 (baseURL, 인터셉터 통일)
                    const response = await instance.post('/auth/refresh-token', null, {
                        headers: { Authorization: `Bearer ${refreshToken}` }
                    });

                    const newAccessToken = response.data.retObj?.accessToken;

                    if (newAccessToken) {
                        // [BUG FIX] localStorage 저장 추가 (기존엔 axios.defaults 에만 설정)
                        localStorage.setItem('accessToken', newAccessToken);
                        instance.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

                        // [BUG FIX] axios → instance 로 변경하여 재시도
                        return instance(originalRequest);
                    } else {
                        // 새 accessToken 이 없으면 재로그인
                        console.warn("새 accessToken 발급 실패 → 로그인 페이지로 이동");
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        window.location.href = '/loginPage';
                    }
                }
            }
        } catch (retryError) {
            // refresh-token 요청 자체가 실패한 경우 (401 또는 네트워크 오류)
            console.error("refresh-token 재발급 실패:", retryError);
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/loginPage';
        }

        return Promise.reject(error);
    }
);

export default instance;
